// js/address-validation-admin.js
import { validateAddress } from './address-validator';

/**
 * Attach "Validate Address" button logic for Admin user view.
 * Expects a button with id "validate-address-btn" carrying data-endpoint
 * and data-address1, data-address2, data-city, data-state, data-postal-code
 * attributes for the current address on file.
 */
export function attachAdminAddressValidation() {
    const btn = document.getElementById('validate-address-btn');
    if (!btn) return;

    const { dataset: { endpoint } } = btn;
    if (!endpoint) {
        console.error('No endpoint specified');
        console.error('Cannot run validation.');
        return;
    }

    const modalEl = document.getElementById('address-modal');

    // Wire up modal buttons (once)
    if (modalEl) {
        wireModalButtons(modalEl);
    }

    btn.addEventListener('click', async () => {
        try {
            await validateAddress({
                button: btn,
                endpoint,
                onSuccess: (data) => successCallback(data, btn, modalEl),
                onError: error => errorCallback(error)
            });
        } catch (error) {
            console.error('Unexpected error during address validation:', error);
        }
    });
}

/**
 * Format an address object as HTML for the modal
 */
/**
 * @param {Object} addr      Address to render
 * @param {Object} [compare] Other address to diff against — differing parts get <mark>
 */
function formatAddress(addr, compare = null) {
    // Default country to 'US' if missing
    const normalized = Object.assign({ country: 'US' }, addr);
    const normalizedCompare = compare ? Object.assign({ country: 'US' }, compare) : null;

    const f = (field) => {
        const val = (normalized[field] || '').trim();
        if (!normalizedCompare) return escapeHtml(val);
        const other = (normalizedCompare[field] || '').trim();
        if (val.toLowerCase() === other.toLowerCase()) return escapeHtml(val);
        return `<mark style="background:#fff3cd; padding:0 0.2rem; border-radius:2px;">${escapeHtml(val)}</mark>`;
    };

    let html = `<p class="confirmed-address-line">${f('address1')}</p>`;
    if (normalized.address2) {
        html += `<p class="confirmed-address-line">${f('address2')}</p>`;
    }
    html += `<p class="confirmed-address-line">${f('city')}, ${f('state')} ${f('postal_code')}</p>`;
    html += `<p class="confirmed-address-line">${f('country')}</p>`;
    return html;
}

/**
 * Compare two address objects field-by-field (case-insensitive, trimmed)
 */
function addressesMatch(a, b) {
    const fields = ['address1', 'address2', 'city', 'state', 'postal_code'];
    return fields.every(f =>
        (a[f] || '').trim().toLowerCase() === (b[f] || '').trim().toLowerCase()
    );
}

/**
 * Escape HTML entities
 */
function escapeHtml(str) {
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(str || ''));
    return div.innerHTML;
}

/**
 * Read the current address from the validate button's data attributes
 */
function getEnteredAddress(btn) {
    return {
        address1: btn.dataset.address1 || '',
        address2: btn.dataset.address2 || '',
        city: btn.dataset.city || '',
        state: btn.dataset.state || '',
        postal_code: btn.dataset.postalCode || '',
        country: btn.dataset.country || ''
    };
}

/**
 * Open a Foundation Reveal modal using a hidden data-open trigger (no jQuery)
 */
function openModal(modalEl) {
    const trigger = document.createElement('button');
    trigger.setAttribute('data-open', modalEl.id);
    trigger.style.display = 'none';
    document.body.appendChild(trigger);
    trigger.click();
    // Clean up the temporary trigger after a tick
    setTimeout(() => trigger.remove(), 100);
}

/**
 * Close a Foundation Reveal modal using a hidden data-close trigger (no jQuery)
 */
function closeModal(modalEl) {
    const trigger = document.createElement('button');
    trigger.setAttribute('data-close', '');
    trigger.style.display = 'none';
    modalEl.appendChild(trigger);
    trigger.click();
    setTimeout(() => trigger.remove(), 100);
}

/**
 * Success callback: show comparison modal instead of auto-accepting.
 * The server has already saved the validated result. The modal lets the
 * admin review the comparison before the UI updates.
 */
function successCallback(data, btn, modalEl) {
    const validatedAddress = data.validated_address;
    if (!validatedAddress) {
        applyValidatedBadge(btn);
        return;
    }

    // If no modal partial is on the page, fall back to immediate badge update
    if (!modalEl) {
        applyValidatedBadge(btn);
        return;
    }

    const enteredAddress = getEnteredAddress(btn);

    const matchSection = document.getElementById('address-match-section');
    const diffSection = document.getElementById('address-diff-section');
    const confirmedOutput = document.getElementById('confirmed-address-output');
    const enteredOutput = document.getElementById('entered-address-output');
    const validatedOutput = document.getElementById('validated-address-output');

    if (addressesMatch(enteredAddress, validatedAddress)) {
        // Addresses match: show simple confirmation
        if (matchSection) matchSection.style.display = 'block';
        if (diffSection) diffSection.style.display = 'none';
        if (confirmedOutput) confirmedOutput.innerHTML = formatAddress(validatedAddress);
    } else {
        // Addresses differ: show side-by-side comparison
        if (matchSection) matchSection.style.display = 'none';
        if (diffSection) diffSection.style.display = 'block';
        if (enteredOutput) enteredOutput.innerHTML = formatAddress(enteredAddress, validatedAddress);
        if (validatedOutput) validatedOutput.innerHTML = formatAddress(validatedAddress, enteredAddress);
    }

    // Hide "Go Back and Edit" if no validation attempts remain
    const editBtn = modalEl.querySelector('[data-action="edit-address"]');
    if (editBtn) {
        editBtn.style.display = data.remaining_validations > 0 ? '' : 'none';
    }

    // Show mock mode notice if applicable
    const mockNotice = modalEl.querySelector('#mock-mode-notice');
    if (data.mock && mockNotice) {
        mockNotice.style.display = 'block';
    } else if (mockNotice) {
        mockNotice.style.display = 'none';
    }

    // Store references for modal button handlers
    modalEl._adminBtn = btn;
    modalEl._validatedAddress = validatedAddress;

    openModal(modalEl);
}

/**
 * Wire up the modal action buttons (called once at init)
 */
function wireModalButtons(modalEl) {
    // "Use Verified Address" / "Continue" — save verified address fields to DB
    modalEl.querySelectorAll('[data-action="confirm-address"]').forEach(confirmBtn => {
        confirmBtn.addEventListener('click', async () => {
            confirmBtn.disabled = true;
            confirmBtn.textContent = 'Saving\u2026';
            const btn = modalEl._adminBtn;
            const addressId = btn?.dataset.endpoint?.match(/id=(\d+)/)?.[1];
            if (addressId && modalEl._validatedAddress) {
                await saveAddressChoice(addressId, 'verified', modalEl._validatedAddress);
            }
            updateShippingAddressDisplay(modalEl._validatedAddress);
            applyValidatedBadge(btn, 'Validated', 'success');
            // Show revert button with validated address as the new "current"
            const revertBtn = document.getElementById('revert-to-original-btn');
            if (revertBtn && modalEl._validatedAddress) {
                const va = modalEl._validatedAddress;
                revertBtn.dataset.address1   = va.address1    || '';
                revertBtn.dataset.address2   = va.address2    || '';
                revertBtn.dataset.city       = va.city        || '';
                revertBtn.dataset.state      = va.state       || '';
                revertBtn.dataset.postalCode = va.postal_code || '';
                revertBtn.dataset.country    = va.country     || '';
                revertBtn.style.display = '';
            }
            document.getElementById('swap-to-validated-btn')?.style && (document.getElementById('swap-to-validated-btn').style.display = 'none');
            closeModal(modalEl);
            showValidationNotice('Melissa Data verified address applied.', 'success');
        });
    });

    // "Use My Address" — save as user_override
    const useEnteredBtn = modalEl.querySelector('[data-action="use-entered-address"]');
    if (useEnteredBtn) {
        useEnteredBtn.addEventListener('click', async () => {
            useEnteredBtn.disabled = true;
            useEnteredBtn.textContent = 'Saving\u2026';
            const btn = modalEl._adminBtn;
            const addressId = btn?.dataset.endpoint?.match(/id=(\d+)/)?.[1];
            if (addressId) {
                await saveAddressChoice(addressId, 'user_override', modalEl._validatedAddress);
            }
            applyValidatedBadge(btn, 'User Override', 'warning');
            // Show swap button with the entered (now user_override) address as "current"
            const swapBtn = document.getElementById('swap-to-validated-btn');
            if (swapBtn) {
                const ea = getEnteredAddress(btn);
                swapBtn.dataset.address1   = ea.address1    || '';
                swapBtn.dataset.address2   = ea.address2    || '';
                swapBtn.dataset.city       = ea.city        || '';
                swapBtn.dataset.state      = ea.state       || '';
                swapBtn.dataset.postalCode = ea.postal_code || '';
                swapBtn.dataset.country    = ea.country     || '';
                swapBtn.style.display = '';
            }
            document.getElementById('revert-to-original-btn')?.style && (document.getElementById('revert-to-original-btn').style.display = 'none');
            closeModal(modalEl);
            showValidationNotice('Original address kept (user override).', 'warning');
        });
    }

    // "Go Back and Edit" — just close, no action
    const editBtn = modalEl.querySelector('[data-action="edit-address"]');
    if (editBtn) {
        editBtn.addEventListener('click', () => {
            closeModal(modalEl);
        });
    }
}

/**
 * Save the admin's address choice to the server
 */
async function saveAddressChoice(addressId, choice, validatedAddress = null) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';
    try {
        const body = { choice };
        if (validatedAddress) {
            body.validated_address = validatedAddress;
        }
        await fetch(`?page=Admin/saveAddressChoice&id=${addressId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-Token': csrfToken,
            },
            body: JSON.stringify(body),
        });
    } catch (err) {
        console.error('[saveAddressChoice]', err);
    }
}

/**
 * Show a brief inline notice below the address card after a validation choice is saved.
 * Reuses the existing #validation-notice div.
 */
function showValidationNotice(message, type) {
    const el = document.getElementById('validation-notice');
    if (!el) return;
    el.className = 'callout ' + (type === 'success' ? 'success' : 'warning');
    el.textContent = message;
    el.style.display = 'block';
    setTimeout(() => { el.style.display = 'none'; }, 6000);
}

/**
 * Update the shipping address text on the page to reflect the chosen address.
 */
function updateShippingAddressDisplay(addr) {
    const el = document.getElementById('shipping-address-display');
    if (!el || !addr) return;

    const lines = [addr.address1];
    if (addr.address2) lines.push(addr.address2);
    lines.push(`${addr.city}, ${addr.state}  ${addr.postal_code}`);
    if (addr.country) lines.push(addr.country);
    el.innerHTML = lines.map(l => escapeHtml(l)).join('<br>');
}

/**
 * Update the UI to show validated state: remove button, update badge
 */
function applyValidatedBadge(btn, label = 'Validated', badgeClass = 'success') {
    if (btn) {
        btn.remove();
    }

    const badge = document.querySelector('#address-badge');
    if (badge) {
        badge.textContent = label;
        badge.classList.remove('warning', 'success', 'alert');
        badge.classList.add(badgeClass);
    }
}

/**
 * Error callback after address validation
 */
function errorCallback(error) {
    const btn = document.getElementById('validate-address-btn');
    const message = error.message || 'Address validation failed.';
    const notice = document.getElementById('validation-notice');

    // If validation is disabled (US or international), show persistent notice
    if (error.isDisabled || message.includes('currently disabled') || error.international) {
        if (btn) {
            btn.disabled = true;
        }
        if (notice) {
            notice.textContent = message;
            notice.style.display = 'block';
        }
        return;
    }

    alert(message);
}
